import { useEffect, useMemo } from 'react';
import { FilterableHistoryType } from 'src/@types/historyTables';
import useHistoryTableFilterStore from 'src/stores/historyTableFilterStore/useHistoryTableFilterStore';
import { useShortTermHistoryQuery } from './useShortTermHistoryQuery';
import { useLongTermHistoryQuery } from './useLongTermHistoryQuery';
import { useHistoryPagination } from './useHistoryPagination';
import type { HistoryDataState } from './historyDataTypes';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyDeserializer<T> = (r: any) => T;

/**
 * Composes short-term and long-term history queries with pagination.
 * Automatically switches between short-term and long-term based on filter mode.
 */
export const useComposedHistory = <T>(
	historyType: FilterableHistoryType,
	deserializer: AnyDeserializer<T>
): {
	historyData: HistoryDataState<T>;
	executePageChange: (newPage: number, uiPageSize?: number) => void;
	refetch: () => void;
} => {
	const mode = useHistoryTableFilterStore((s) =>
		s.getFilterState(historyType).getFilterMode()
	);

	const isShortTerm = mode === 'SHORT_TERM';

	// --- Short-term ---
	const shortTermQuery = useShortTermHistoryQuery<T>(
		historyType,
		deserializer,
		isShortTerm
	);
	const shortTermPagination = useHistoryPagination(historyType);

	useEffect(() => {
		if (!isShortTerm) return;
		if (
			shortTermPagination.currentApiPage >= shortTermQuery.totalPagesFetched &&
			shortTermQuery.hasNextPage
		) {
			shortTermQuery.fetchNextPage();
		}
	}, [
		isShortTerm,
		shortTermPagination.currentApiPage,
		shortTermQuery.totalPagesFetched,
		shortTermQuery.hasNextPage,
		shortTermQuery.fetchNextPage,
	]);

	// --- Long-term ---
	const longTermQuery = useLongTermHistoryQuery<T>(
		historyType,
		deserializer,
		!isShortTerm
	);

	// --- totalCount for short-term ---
	const hasLoadedNextPage =
		shortTermPagination.currentApiPage + 1 < shortTermQuery.totalPagesFetched;
	const shortTermTotalCount = hasLoadedNextPage
		? shortTermQuery.allRecords.length
		: shortTermQuery.allRecords.length + (shortTermQuery.hasNextPage ? 1 : 0);

	const historyData: HistoryDataState<T> = useMemo(() => {
		if (isShortTerm) {
			return {
				records: shortTermQuery.allRecords,
				totalCount: shortTermTotalCount,
				loading: shortTermQuery.isLoading,
				initialHistoryLoaded: shortTermQuery.isFetched,
			};
		}
		return {
			records: longTermQuery.allRecords,
			totalCount: longTermQuery.allRecords.length,
			loading: longTermQuery.isLoading,
			initialHistoryLoaded: longTermQuery.isFetched,
		};
	}, [
		isShortTerm,
		shortTermQuery.allRecords,
		shortTermTotalCount,
		shortTermQuery.isLoading,
		shortTermQuery.isFetched,
		longTermQuery.allRecords,
		longTermQuery.isLoading,
		longTermQuery.isFetched,
	]);

	const refetch = isShortTerm ? shortTermQuery.refetch : longTermQuery.refetch;

	const executePageChange = shortTermPagination.executePageChange;

	return { historyData, executePageChange, refetch };
};
